import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import Dropdown from "@/components/ui/Dropdown";
import {
  createSubscriptionPlan,
  updateSubscriptionPlan,
  getMasterDropdownService,
  getCitiesBasedOnState,
} from "@/services/masters";
import { FormField } from "@/components/ui/FormField";
import { Dialog, Transition } from "@headlessui/react";

// ─── Static duration config ───────────────────────────────────────────────────
const DURATIONS = [
  { id: "1", label: "1 Month", period: "1 Month", days: 30, months: 1 },
  { id: "3", label: "3 Months", period: "3 Months", days: 90, months: 3 },
  { id: "6", label: "6 Months", period: "6 Months", days: 180, months: 6 },
  { id: "10", label: "10 Months", period: "10 Months", days: 300, months: 10 },
];

const DISTANCE_UNITS = ["Meter", "KM"];
const BASE_DIST_UNITS = ["KM", "Meter"];

// ─── Zod schema ───────────────────────────────────────────────────────────────
const subscriptionSchema = z.object({
  State: z.string().min(1, "State is required"),
  City: z.string().min(1, "City is required"),
  Plan_Name: z.string().min(1, "Plan is required"),
  Base_Price: z.string().min(1, "Base price is required"),
  Base_Distance: z.string().min(1, "Base distance is required"),
  Base_Distance_Units: z.string().min(1, "Base distance unit is required"),
  Distance_Step: z.string().min(1, "Distance step is required"),
  Distance_Step_Units: z.string().min(1, "Distance step unit is required"),
  Rate_Per_Meter: z.string().min(1, "Rate per meter is required"),
  duration_discounts: z.record(z.string(), z.string()),
});

type SubscriptionFormData = z.infer<typeof subscriptionSchema>;

// ─── Props ────────────────────────────────────────────────────────────────────
interface SubscriptionModalProps {
  subscription: any | null;
  onClose: () => void;
  onSuccess: () => void;
  isOpen: boolean;
}

type StateOption = {
  Name: string;
  unique_code: string;
};

type CityOption = {
  City_Name: string;
  unique_code: string;
};

type ApiErrorResponse = {
  non_field_errors?: string[];
  message?: string;
  [key: string]:
  string[] |
  string |
  undefined;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const defaultDiscounts = (): Record<string, string> => ({
  "1": "0",
  "3": "5",
  "6": "10",
  "10": "15",
});

function buildRanges(
  basePrice: number,
  baseDistVal: string,
  baseDistUnit: string,
  discounts: Record<string, string>
) {
  const kmRange = `${baseDistVal} ${baseDistUnit}`;
  return DURATIONS.map((d) => {
    const total = basePrice * d.months;
    const discPct = parseFloat(discounts[d.id] ?? "0") || 0;
    const discAmt = (total * discPct) / 100;
    const finalAmt = total - discAmt;
    return {
      Period: d.period,
      KM_Range: kmRange,
      Base_Price: total.toFixed(2),
      Final_Amount: finalAmt.toFixed(2),
      Discount_Amount: discAmt.toFixed(2),
      Discount_Percentage: discPct,
    };
  });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function SubscriptionModal({
  subscription,
  isOpen,
  onClose,
  onSuccess,
}: SubscriptionModalProps) {
  const isEditing = !!subscription;

  const [states, setStates] =
    useState<StateOption[]>([]);

  const [cities, setCities] =
    useState<CityOption[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const skipCityClearRef = useRef(false);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    setError,

    formState: {
      isSubmitting,
      errors
    }
  } = useForm<SubscriptionFormData>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      State: "",
      City: "",
      Plan_Name: "",
      Base_Price: "",
      Base_Distance: "",
      Base_Distance_Units: "KM",
      Distance_Step: "",
      Distance_Step_Units: "Meter",
      Rate_Per_Meter: "",
      duration_discounts: defaultDiscounts(),
    },
  });

  const selectedState = watch("State");
  const planName = watch("Plan_Name");
  const monthlyBasePrice = watch("Base_Price");
  const baseDistVal = watch("Base_Distance");
  const baseDistUnit = watch("Base_Distance_Units");
  const durationDiscounts = watch("duration_discounts");

  // ── Reset & close ────────────────────────────────────────────────────────
  const handleClose = () => {
    skipCityClearRef.current = false;
    setCities([]);
    reset({
      State: "",
      City: "",
      Plan_Name: "",
      Base_Price: "",
      Base_Distance: "",
      Base_Distance_Units: "KM",
      Distance_Step: "",
      Distance_Step_Units: "Meter",
      Rate_Per_Meter: "",
      duration_discounts: defaultDiscounts(),
    });
    onClose();
  };

  const stateCodeMap =
    new Map<string, string>(
      states.map(
        (
          s
        ) => [
            s.Name,
            s.unique_code
          ]
      )
    );

  // ── Load states on mount ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingData(true);
        const res = await getMasterDropdownService();
        if (res.status === 200) {
          const response = res.data as any;
          setStates(response.States || []);
        }
      } catch {
        toast.error("Failed loading states");
      } finally {
        setLoadingData(false);
      }
    })();
  }, []);

  // ── Load cities when state changes ───────────────────────────────────────
  useEffect(() => {

    if (!selectedState) {

      setCities([]);
      setValue("City", "");

      return;
    }

    const loadCities =
      async () => {

        try {

          const stateCode =
            stateCodeMap.get(
              selectedState
            );

          if (!stateCode) {

            setCities([]);

            return;
          }

          const res =
            await getCitiesBasedOnState(
              stateCode
            );
          const response = res.data as any;
          setCities(
            response.Cities
            ?? []
          );

        } catch {

          toast.error(
            "Failed loading cities"
          );

          setCities([]);

        }

      };

    loadCities();

  }, [
    selectedState,
    setValue
  ]);

  // ── Populate form when editing ───────────────────────────────────────────
  useEffect(() => {
    if (!subscription) return;

    const editDiscounts: Record<string, string> = { ...defaultDiscounts() };

    if (Array.isArray(subscription.Ranges)) {
      const periodMap = new Map<string, any>();
      subscription.Ranges.forEach((r: any) => {
        if (!periodMap.has(r.Period)) periodMap.set(r.Period, r);
      });
      DURATIONS.forEach((d) => {
        const r = periodMap.get(d.period);
        if (r) editDiscounts[d.id] = r.Discount_Percentage?.toString() ?? "0";
      });
    }

    const stateVal = subscription.State ?? "";
    const cityVal = subscription.City ?? "";

    // Step 1 — reset everything except City (City dropdown needs options first)
    // Mark that the upcoming selectedState change is from edit-load, not user action
    skipCityClearRef.current = true;

    reset({
      State:
        states.find(
          s =>
            s.unique_code ===
            stateVal
        )?.Name
        ||
        stateVal,

      City:
        cityVal,

      Plan_Name:
        subscription.Plan_Name,

      Base_Price:
        String(
          subscription.Base_Price
        ),

      Base_Distance:
        String(
          subscription.Base_Distance
        ),

      Base_Distance_Units:
        subscription.Base_Distance_Units,

      Distance_Step:
        String(
          subscription.Distance_Step
        ),

      Distance_Step_Units:
        subscription.Distance_Step_Units,

      Rate_Per_Meter:
        String(
          subscription.Rate_Per_Meter
        ),

      duration_discounts:
        editDiscounts
    });

    // Step 2 — if state is set, load its cities; after they arrive the Dropdown
    // will re-render with options and the already-set City value will match.
    if (stateVal) {

      const stateCode =
        states.find(
          s =>
            s.Name ===
            stateVal
        )?.unique_code;

      if (stateCode) {

        getCitiesBasedOnState(
          stateCode
        )

          .then(
            (res) => {
              const response = res.data as any;
              setCities(
                response.Cities
                ?? []
              );

              setValue(
                "City",
                cityVal
              );

            }
          );

      }

    }
  }, [subscription]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const onSubmit = async (data: SubscriptionFormData) => {
    clearErrors();
    try {
      const basePrice = parseFloat(data.Base_Price);
      const ranges = buildRanges(
        basePrice,
        data.Base_Distance,
        data.Base_Distance_Units,
        data.duration_discounts
      );

      const payload = {
        State: data.State,
        City: data.City,
        Plan_Name: data.Plan_Name,
        Base_Price: basePrice,
        Base_Distance: Number(data.Base_Distance),
        Base_Distance_Units: data.Base_Distance_Units,
        Distance_Step: Number(data.Distance_Step),
        Distance_Step_Units: data.Distance_Step_Units,
        Rate_Per_Meter: Number(data.Rate_Per_Meter),
        Ranges: ranges,
      };

      const response = isEditing
        ? await updateSubscriptionPlan(subscription.id, payload)
        : await createSubscriptionPlan(payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(isEditing ? "Plan updated!" : "Plan created!");
        onSuccess();
      } else {

        const apiErrors =
          response?.errors;
        // console.log(apiErrors, "API ERRORS")
        // non field validation
        if (
          Array.isArray(
            apiErrors?.non_field_errors
          )
        ) {

          const msg =
            apiErrors
              .non_field_errors
              .join("\n");
          // console.log(msg, 'msg')
          toast.error(
            msg
          );

          return;
        }

        // field validation
        if (
          apiErrors &&
          typeof apiErrors ===
          "object"
        ) {

          Object.entries(
            apiErrors
          ).forEach(
            ([key, value]) => {

              if (
                Array.isArray(
                  value
                )
              ) {

                const msg =
                  String(
                    value[0]
                  );

                setError(
                  key as any,
                  {
                    type:
                      "server",

                    message:
                      msg
                  }
                );

                toast.error(
                  `${key}: ${msg}`
                );

              }

            }
          );

          return;
        }

        toast.error(
          "Failed to save plan"
        );

        return;

      }
    } catch (error: any) {

      console.log(
        error?.response?.data,
        "API ERROR"
      );

      const apiErrors =
        error?.response?.data;

      // Handle non_field_errors
      if (
        Array.isArray(
          apiErrors?.non_field_errors
        )
      ) {

        const msg =
          apiErrors
            .non_field_errors
            .join("\n");

        toast.dismiss();

        setTimeout(() => {
          toast.error(
            msg,
            {
              duration: 5000
            }
          );
        }, 100);

        return;
      }

      // Handle field errors
      if (
        apiErrors &&
        typeof apiErrors ===
        "object"
      ) {

        Object.entries(
          apiErrors
        ).forEach(
          ([key, value]) => {

            if (
              Array.isArray(
                value
              )
            ) {

              const msg =
                String(
                  value[0]
                );

              setError(
                key as any,
                {
                  type:
                    "server",

                  message:
                    msg
                }
              );

              toast.dismiss();

              setTimeout(
                () =>
                  toast.error(
                    msg
                  ),
                100
              );
            }

          }
        );

        return;
      }

      toast.error(
        "Save failed"
      );

    }
  };
  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 p-4 sm:p-6">
          <div className="flex min-h-screen items-center justify-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl bg-white rounded-xl shadow-2xl flex flex-col max-h-[90vh]">

                {/* ── Header ─────────────────────────────────────────────── */}
                <div
                  className="flex items-center justify-between p-6 rounded-t-xl"
                  style={{ background: "linear-gradient(135deg, #2d3440 0%, #535c6b 100%)" }}
                >
                  <Dialog.Title as="h2" className="text-2xl font-bold text-white">
                    {isEditing ? "Edit Subscription Plan" : "Add Subscription Plan"}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* ── Scrollable body ─────────────────────────────────────── */}
                <div className="flex-1 flex overflow-hidden">
                  <form
                    onSubmit={
                      handleSubmit(

                        onSubmit,

                        (formErrors) => {

                          Object.values(
                            formErrors
                          ).forEach(
                            (err: any) => {

                              if (
                                err?.message
                              ) {

                                toast.error(
                                  err.message
                                );

                              }

                            }
                          );

                        }
                      )
                    }
                    className="flex-1 flex flex-col overflow-hidden w-full"
                  >
                    <div className="flex-1 overflow-y-auto px-6 py-8 scrollbar-thin scrollbar-thumb-gray-300">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                        {/* ── LEFT: Plan details ─────────────────────────── */}
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-6">Plan Details</h3>
                          <div className="space-y-5">

                            {/* State */}
                            <Dropdown
                              control={control}
                              name="State"
                              label="State"
                              required
                              isLoading={loadingData}
                              options={states.map((s) => ({
                                id: s.unique_code,
                                value: s.Name,
                                label: s.Name
                              }))}
                              placeholder="Select State"
                            />
                            {
                              errors.State && (
                                <p className="text-red-500 text-xs">
                                  {
                                    errors.State.message
                                  }
                                </p>
                              )
                            }
                            {/* City */}
                            <Dropdown
                              control={control}
                              name="City"
                              label="City"
                              required
                              disabled={!selectedState}
                              options={cities.map((c) => ({
                                id: c.unique_code,
                                value: c.City_Name,
                                label: c.City_Name
                              }))}
                              placeholder="Select City"
                            />
                            {
                              errors.City && (
                                <p className="text-red-500 text-xs">
                                  {
                                    errors.City.message
                                  }
                                </p>
                              )
                            }

                            {/* Plan Name toggle */}
                            <div>
                              <label className="block text-sm font-bold text-gray-800 mb-2">
                                Plan Name <span className="text-red-500">*</span>
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                {["Basic", "Premium"].map((plan) => (
                                  <button
                                    key={plan}
                                    type="button"
                                    onClick={() => setValue("Plan_Name", plan)}
                                    disabled={isEditing}
                                    className={`py-3 rounded-lg font-semibold transition-all text-sm ${planName === plan
                                      ? "bg-brand-500 text-white shadow"
                                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  >
                                    {plan} Plan
                                  </button>
                                ))}
                              </div>
                              {errors.Plan_Name && (
                                <p className="text-red-500 text-xs mt-1">{errors.Plan_Name.message}</p>
                              )}
                            </div>

                            {/* Base Price */}
                            <div>
                              <FormField
                                control={control}
                                name="Base_Price"
                                label="Base Price (₹)"
                                type="text"
                                placeholder="e.g., 599"
                                required
                                error={errors.Base_Price?.message}
                              />
                              <p className="text-xs text-gray-500 mt-1">Price for 1 KM</p>
                            </div>

                            {/* Base Distance + Units side by side */}
                            <div className="grid grid-cols-2 gap-3">
                              <FormField
                                control={control}
                                name="Base_Distance"
                                label="Base Distance"
                                type="text"
                                placeholder="e.g., 1"
                                required
                                error={errors.Base_Distance?.message}
                              />
                              <div>
                                <label className="block text-sm font-bold text-gray-800 mb-2">
                                  Unit <span className="text-red-500">*</span>
                                </label>
                                <Controller
                                  name="Base_Distance_Units"
                                  control={control}
                                  render={({ field }) => (
                                    <select
                                      {...field}
                                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                                    >
                                      {BASE_DIST_UNITS.map((u) => (
                                        <option key={u} value={u}>{u}</option>
                                      ))}
                                    </select>
                                  )}
                                />
                              </div>
                            </div>

                            {/* Distance Step + Units side by side */}
                            <div className="grid grid-cols-2 gap-3">
                              <FormField
                                control={control}
                                name="Distance_Step"
                                label="Distance Step"
                                type="text"
                                placeholder="e.g., 0.1"
                                required
                                error={errors.Distance_Step?.message}
                              />
                              <div>
                                <label className="block text-sm font-bold text-gray-800 mb-2">
                                  Unit <span className="text-red-500">*</span>
                                </label>
                                <Controller
                                  name="Distance_Step_Units"
                                  control={control}
                                  render={({ field }) => (
                                    <select
                                      {...field}
                                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                                    >
                                      {DISTANCE_UNITS.map((u) => (
                                        <option key={u} value={u}>{u}</option>
                                      ))}
                                    </select>
                                  )}
                                />
                              </div>
                            </div>

                            {/* Rate Per Meter */}
                            <FormField
                              control={control}
                              name="Rate_Per_Meter"
                              label="Rate Per Meter (₹)"
                              type="text"
                              placeholder="e.g., 0.118"
                              required
                              error={errors.Rate_Per_Meter?.message}
                            />

                            {/* Plan preview */}
                            {planName && monthlyBasePrice && (
                              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                                <p className="text-sm font-bold text-brand-900 mb-2">Plan Preview</p>
                                <p className="text-sm text-gray-700">
                                  Plan: <span className="font-semibold">{planName}</span>
                                </p>
                                <p className="text-sm text-gray-700">
                                  Base Price: <span className="font-bold">₹{monthlyBasePrice}</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Discounts applied per duration on the right →
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ── RIGHT: Duration discounts ──────────────────── */}
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1">Duration Discounts</h3>
                          <p className="text-sm text-gray-500 mb-5">
                            Set discount % per duration. Ranges are auto-calculated and included in the payload.
                          </p>

                          {!monthlyBasePrice ? (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                              <p className="text-sm text-yellow-700 font-semibold">
                                Enter a base price to preview pricing breakdowns
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {DURATIONS.map((duration) => {
                                const base = parseFloat(monthlyBasePrice) * duration.months;
                                const discPct = parseFloat(durationDiscounts?.[duration.id] ?? "0") || 0;
                                const discAmt = (base * discPct) / 100;
                                const finalPrice = base - discAmt;

                                return (
                                  <div key={duration.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                    {/* Row: label + discount input */}
                                    <div className="flex items-center justify-between mb-3">
                                      <div>
                                        <span className="font-bold text-gray-900 text-sm">{duration.label}</span>
                                        <span className="text-xs text-gray-400 ml-1">({duration.days} days)</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Controller
                                          name={`duration_discounts.${duration.id}` as any}
                                          control={control}
                                          render={({ field }) => (
                                            <input
                                              {...field}
                                              type="text"
                                              step="1"
                                              min="0"
                                              max="100"
                                              placeholder="0"
                                              className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm text-center"
                                            />
                                          )}
                                        />
                                        <span className="text-sm text-gray-500">%</span>
                                      </div>
                                    </div>

                                    {/* Pricing breakdown */}
                                    <div className="space-y-1.5 border-t border-gray-100 pt-3">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Base Price</span>
                                        <span className="font-semibold text-gray-900">₹{base.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span className="text-orange-500">Discount ({discPct}%)</span>
                                        <span className="font-semibold text-orange-500">- ₹{discAmt.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between text-sm pt-1.5 border-t border-gray-100">
                                        <span className="font-bold text-gray-900">Final Price</span>
                                        <span className="font-bold text-green-600 text-base">₹{finalPrice.toFixed(2)}</span>
                                      </div>
                                    </div>

                                    {/* KM Range badge */}
                                    {baseDistVal && baseDistUnit && (
                                      <div className="mt-2 pt-2 border-t border-dashed border-gray-100">
                                        <span className="text-xs text-gray-400">KM Range: </span>
                                        <span className="text-xs font-medium text-gray-600">
                                          {baseDistVal} {baseDistUnit}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Footer actions ────────────────────────────────────── */}
                    <div className="border-t border-gray-200 bg-white p-6 rounded-b-xl">
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={isSubmitting}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-mlb-gold hover:bg-mlb-gold/80 active:bg-mlb-gold/70 text-brand-700 font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                        >
                          {isSubmitting
                            ? "Saving…"
                            : isEditing
                              ? "Update Plan"
                              : "Add Plan"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}